import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { logError } from "@/lib/log";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { SPECIALITIES } from "@/components/SpecialityAccessFields";
import { Mail, RefreshCw, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  active: { label: "Password set", cls: "bg-emerald-100 text-emerald-800" },
  pending: { label: "Awaiting password", cls: "bg-amber-100 text-amber-800" },
  expired: { label: "Link expired", cls: "bg-rose-100 text-rose-800" },
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

function ResendResult({ r }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => { await navigator.clipboard.writeText(r.invite_link); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="mt-2 flex gap-2 items-center" data-testid={`resend-result-${r.id}`}>
      <Input readOnly value={r.invite_link} className="font-mono text-xs bg-white h-8" onFocus={(e) => e.target.select()} data-testid={`resend-link-${r.id}`} />
      <Button type="button" size="sm" variant="outline" onClick={copy} data-testid={`resend-copy-${r.id}`}>{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</Button>
    </div>
  );
}

export default function AdminInviteList({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [results, setResults] = useState({});
  const load = useCallback(() => api.get("/admin/invites").then((r) => setRows(r.data)).catch((e) => logError("admin-invites", e)), []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const resend = async (row) => {
    setBusy(row.id);
    try {
      const { data } = await api.post(`/admin/invites/${row.id}/resend`);
      toast.success(data.email_sent ? `New link emailed to ${row.email}` : "New link created — copy it below");
      setResults((r) => ({ ...r, [row.id]: data })); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not resend"); }
    finally { setBusy(null); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-invite-list">
      <p className="font-display font-semibold flex items-center gap-2 mb-1"><Mail className="w-4 h-4 text-accent" /> Invited accounts</p>
      <p className="text-xs text-muted-foreground mb-4">Everyone you've invited, whether they've set a password yet, and a one-click resend when their 7-day link has expired.</p>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="admin-invite-list-empty">No invites sent yet.</p> : (
        <ul className="divide-y divide-border">
          {rows.map((r) => {
            const st = STATUS[r.status]; const spec = SPECIALITIES.find(([v]) => v === r.speciality)?.[1];
            return (
              <li key={r.id} className="py-3 text-sm" data-testid={`invite-row-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{r.name} <span className="text-muted-foreground font-normal">· {r.email}</span></p>
                    <p className="text-xs text-muted-foreground">{spec || "No speciality"}{r.org_name ? ` · ${r.org_name}` : ""} · invited {fmt(r.created_at)}{r.status !== "active" && r.link_expires_at ? ` · link ${r.status === "expired" ? "expired" : "expires"} ${fmt(r.link_expires_at)}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`${st.cls} hover:${st.cls}`} data-testid={`invite-status-${r.id}`}>{st.label}</Badge>
                    {r.status !== "active" && (
                      <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => resend(r)} data-testid={`invite-resend-${r.id}`}>
                        {busy === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Resend link</>}
                      </Button>
                    )}
                  </div>
                </div>
                {results[r.id] && <ResendResult r={results[r.id]} />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
